import React, { useState, useEffect } from "react";
import "./manage-contacts-page.scss";
import DataTable from "react-data-table-component";
import ContactHelper from "../../../../entities/contact/contact-helper";
import { useSelector } from "react-redux";
import AddContactModal from "../../components/add-contact-modal/add-contact-modal";
import EditContactModal from "../../components/edit-contact-modal/edit-contact-modal";
import DeleteContactModal from "../../components/delete-contact-modal/delete-contact-modal";
import { EventEmitter } from "../../../../shared/utils/event-emitter";
import Events from "../../../../const/events";

const ManageContactsPage = () => {
  const [filteredContacts, setFilteredContacts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchText, setSearchText] = useState("");
  const { contacts } = useSelector((state) => state.contactsStore);

  // Function to apply search filter and update contacts list
  const applySearchFilter = (contactsList, searchTerm = searchText) => {
    const filteredList = searchTerm
      ? contactsList.filter(
          (contact) =>
            (contact.name && contact.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (contact.phone && contact.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (contact.email && contact.email.toLowerCase().includes(searchTerm.toLowerCase()))
        )
      : contactsList;

    return filteredList
      .map((contact) => ({
        id: contact._id,
        name: contact.name || "Unknown",
        phone: contact.phone || "N/A",
        email: contact.email || "N/A",
        dateAdded: new Date(contact.createdAt).toLocaleDateString(),
        createdAt: new Date(contact.createdAt),
      }))
      .sort((a, b) => b.createdAt - a.createdAt); // Sort by latest first
  };

  // Function to update and sort contacts
  const updateContacts = (contactsList) => {
    const processedContacts = applySearchFilter(contactsList);
    setFilteredContacts(processedContacts);
  };

  useEffect(() => {
    if (contacts) {
      updateContacts(contacts);
    }
  }, [contacts, searchText]); // Add searchText as dependency to re-filter when search changes

  const handleSearch = (e) => {
    const text = e.target.value;
    setSearchText(text);
    // The useEffect will handle the filtering when searchText changes
  };

  const handleClearSearch = () => {
    setSearchText("");
  };

  useEffect(() => {
    ContactHelper.fetchAllContacts(); // Initial fetch
  }, []);

  const handleContactAdded = () => {
    ContactHelper.fetchAllContacts(); // Refresh contacts after adding a new one
  };

  const handleEditContact = (contact) => {
    setSelectedContact(contact);
    setShowEditModal(true);
  };

  const handleContactUpdated = () => {
    ContactHelper.fetchAllContacts(); // Refresh contacts after updating - search filter will be preserved
  };

  const handleDeleteContact = (contact) => {
    setSelectedContact(contact);
    setShowDeleteModal(true);
  };

  const handleContactDeleted = () => {
    ContactHelper.fetchAllContacts(); // Refresh contacts after deleting
  };

  const handleShowQr = (contact) => {
    if (!contact?.id) return;
    EventEmitter.dispatch(Events.SHOW_QR_CODE_POPUP, {
      id: contact.id,
      title: contact.name || "Contact",
    });
  };

  return (
    <div className="manage-contacts-page">
      <h1 class="m-0 mb-4">Contact lists</h1>

      <div className="chat-search d-flex align-items-center gap-3 mb-4">
        <button
          type="button"
          className="bouncy-button small"
          onClick={() => setShowModal(true)}
        >
          <i className="mdi mdi-plus"></i>
          <span>Add Contact</span>
        </button>
        <div className="search-container flex-grow-1">
          <div className="input-group">
            <span className="input-group-text">
              <i className="mdi mdi-magnify"></i>
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Search contacts by name, phone, or email..."
              value={searchText}
              onChange={handleSearch}
            />
            {searchText && (
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleClearSearch}
                title="Clear search"
              >
                <i className="mdi mdi-close"></i>
              </button>
            )}
          </div>
          {searchText && (
            <div className="search-info mt-2">
              <small className="text-muted">
                Showing {filteredContacts.length} of {contacts?.length || 0} contacts
              </small>
            </div>
          )}
        </div>
      </div>

      <div className="tableWrapper manageContactsTable">
        <DataTable
          columns={[
            { name: "Name", selector: (row) => row.name, sortable: true },
            { name: "Phone", selector: (row) => row.phone, sortable: true },
            { name: "Email", selector: (row) => row.email, sortable: true },
            {
              name: "Date Added",
              selector: (row) => row.dateAdded,
              sortable: true,
            },
            {
              name: "Actions",
              cell: (row) => (
                <div className="d-flex gap-2">
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => handleShowQr(row)}
                    title="Show QR code"
                  >
                    <i className="mdi mdi-qrcode"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => handleEditContact(row)}
                    title="Edit contact name"
                  >
                    <i className="mdi mdi-pencil"></i>
                  </button>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteContact(row)}
                    title="Delete contact"
                  >
                    <i className="mdi mdi-delete"></i>
                  </button>
                </div>
              ),
              ignoreRowClick: true,
              allowOverflow: true,
              button: true,
            },
          ]}
          data={filteredContacts}
          pagination
        />
      </div>

      <AddContactModal
        show={showModal}
        onHide={() => setShowModal(false)}
        onContactAdded={handleContactAdded} // Refresh contacts when a new one is added
      />

      <EditContactModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        contact={selectedContact}
        onContactUpdated={handleContactUpdated}
      />

      <DeleteContactModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        contact={selectedContact}
        onContactDeleted={handleContactDeleted}
      />
    </div>
  );
};

export default ManageContactsPage;
